import React, { useCallback, useState } from "react";
import type { SegmentTrackProductClickedEvent } from "@beautypie/segment/Track/ProductClicked";

import { useLocale } from "hooks";
import { useVariantIds } from "hooks/useVariantIds";
import { addBasketItemsWithSpinner } from "lib/api";
import { Logger } from "lib/logger";
import { ProductCard } from "packages/core-shopping/components/ProductCardV2";
import { track } from "tracking";
import type {
  AvailabilityEnum,
  MultipleProductCard,
  MultipleProductCardVariant,
  SingularProductCard,
  VariantType
} from "@shared/graphql.types";
import { navigateTo } from "@shared/utils.routing";

import type { PurchaseType } from "components/Shop/PDPPage/types";

import { isMultipleProductCard } from "packages/core-shopping/shared/isMultipleProductCard";
import { toProductCardData } from "packages/core-shopping/shared/toProductCardData";
import { toVariantType } from "packages/core-shopping/shared/toVariantType";
import type { AddBasketItemType } from "lib/api/addBasketItems";

type Props = {
  searchTerm: string;
  productCard: MultipleProductCard | SingularProductCard;
  setConfirmation?: React.Dispatch<React.SetStateAction<boolean>>;
  setConfirmationVariant?: React.Dispatch<
    React.SetStateAction<VariantType | null>
  >;
  pdpPurchaseType?: PurchaseType;
  closeSearch?: () => void;
  list: (MultipleProductCard | SingularProductCard)[];
  listPosition: number;
};

const giftMembershipIdstoSkus = {
  "3074457345616768734": "BP_PLUS_GIFT_UK",
  "3074457345616760245": "BP_PLUS_GIFT_US"
};

export const ProductCardSearch: React.FC<Props> = ({
  searchTerm,
  productCard,
  closeSearch,
  setConfirmation,
  setConfirmationVariant,
  list,
  listPosition
}) => {
  const { giftMembershipProductId } = useVariantIds();
  const giftMembershipSku = giftMembershipIdstoSkus[giftMembershipProductId];
  const isGiftMembership = giftMembershipSku === productCard.variants[0].sku;

  const locale = useLocale();

  const [activeShade] = useState<MultipleProductCardVariant | null>(
    isMultipleProductCard(productCard) ? productCard.variants[0] : null
  );

  const productCardData = {
    ...toProductCardData(productCard, 0),
    inWishlist: false
  };

  const { brandName = "", productName = "" } = productCardData;
  const locationTypeDetails = `${productName} ${brandName} ${
    activeShade?.shade ? activeShade?.shade : ""
  }`;

  const addToBag = useCallback(async () => {
    const locationType = "Search Modal";

    if (isGiftMembership) {
      await addBasketItemsWithSpinner(
        [
          {
            quantity: 1,
            variantId: giftMembershipProductId,
            variantSku:
              locale === "en-US" ? "BP_PLUS_GIFT_US" : "BP_PLUS_GIFT_UK",
            type: "product",
            origin: "user"
          }
        ],
        locationType,
        locationTypeDetails
      )
        .then(() => navigateTo("/basket"))
        .catch(() => Logger.error(`Error adding membership to basket`));

      return;
    }

    const variant = isMultipleProductCard(productCard)
      ? activeShade
      : productCard.variants[0];

    if (!variant) return;

    const items: AddBasketItemType[] = [
      {
        quantity: 1,
        type: "product",
        origin: "user",
        variantSku: variant.sku
      }
    ];

    await addBasketItemsWithSpinner(items, locationType, locationTypeDetails)
      .then(() => {
        const variantType = toVariantType(variant, productCard);
        setConfirmation?.(true);
        setConfirmationVariant?.(variantType);
      })
      .catch(() => Logger.error(`Error adding item to basket`));
  }, [
    locationTypeDetails,
    isGiftMembership,
    productCard,
    activeShade,
    giftMembershipProductId,
    locale,
    setConfirmation,
    setConfirmationVariant
  ]);

  const clickedProductToVariantType: VariantType = {
    sku: productCardData.sku,
    pricing: {
      members: productCardData.primaryPriceRaw,
      typical: productCardData.secondaryPriceRaw,
      guest: productCardData.primaryPriceRaw
    },
    availability: {
      status:
        productCardData.inStock === true
          ? ("inStock" as AvailabilityEnum)
          : ("outOfStock" as AvailabilityEnum),
      stockCount: productCardData.inStock === true ? 999999 : 0
    },
    brandName: productCardData?.brandName ?? "",
    title: productCardData?.productName,
    stockLimit: productCardData.inStock === true ? 999999 : 0,
    visuals: [],
    detailsMedia: {
      id: productCardData.sku,
      src: productCardData.image.src
    }
  };

  const onButtonClick = async () => {
    const isMultivariate = isMultipleProductCard(productCard);
    const variant = productCard.variants[0];

    // if it's a multivariate product, we need to redirect to the first variant href (PDP)
    if (isMultivariate && variant) {
      navigateTo(`/products/${variant.slug}`);
      return;
    }

    closeSearch?.();
    await addToBag();
    setConfirmation?.(true);
    setConfirmationVariant?.(clickedProductToVariantType);
  };

  const trackProductClicked = () => {
    const variant = productCard.variants[0];

    if (!variant) {
      return;
    }

    const productClickedEvent: SegmentTrackProductClickedEvent = {
      event: "Product Clicked",
      properties: {
        sku: variant.sku,
        name: productName,
        brand: brandName,
        variant: variant.shade || productCard.title,
        members_price: variant.pricing.primary.raw,
        typical_price: variant.pricing.secondary.raw,
        url: variant.href,
        currency: variant.pricing.primary.currency,
        result_type: "suggestions"
      }
    };

    track(productClickedEvent, {
      search: { term: searchTerm },
      product: {
        list: {
          targetPosition: listPosition,
          items: list.map((card) => ({ sku: card.variants[0]?.sku }))
        }
      }
    });
  };

  return (
    <ProductCard product={productCardData} onClick={trackProductClicked}>
      <ProductCard.Header>
        <ProductCard.Badge product={productCardData} />
        <ProductCard.Ribbon product={productCardData} />
        <ProductCard.Image product={productCardData} />
      </ProductCard.Header>

      <ProductCard.Body>
        <ProductCard.Rating product={productCardData} />
        <ProductCard.BrandName product={productCardData} />
        <ProductCard.ProductName product={productCardData} />
        <ProductCard.VariationName product={productCardData} />
        <ProductCard.ColorSwatch product={productCardData} />
      </ProductCard.Body>

      <ProductCard.Footer>
        <ProductCard.Button product={productCardData} onClick={onButtonClick} />
      </ProductCard.Footer>
    </ProductCard>
  );
};
